import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'

export type HighRiskOverrideDialogVariant =
  | 'disagreeAutomatedHighRisk'
  | 'disagreeAutomatedNotHighRisk'
  | 'userElevatedHighRisk'

type HighRiskOverrideDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  reason: string
  onReasonChange: (value: string) => void
  isPending: boolean
  onSubmit: () => void
  /** Default: disagree with automated Annex IV high-risk result (record justification to continue as not high-risk). */
  variant?: HighRiskOverrideDialogVariant
  /** When set (typically with `userElevatedHighRisk`), clears override and aligns with the automated check. */
  onAlignWithAutomated?: () => void
}

export const OverrideReasonSchema = z.string().min(10)

export function HighRiskOverrideDialog({
  open,
  onOpenChange,
  reason,
  onReasonChange,
  isPending,
  onSubmit,
  variant = 'disagreeAutomatedHighRisk',
  onAlignWithAutomated,
}: HighRiskOverrideDialogProps) {
  const { t } = useTranslation('machineryDirectiveCompliance')

  const keyPrefix =
    variant === 'userElevatedHighRisk'
      ? 'onboarding.userHighRiskOverrideDialog'
      : variant === 'disagreeAutomatedNotHighRisk'
        ? 'onboarding.notHighRiskDisagreeDialog'
        : 'onboarding.highRiskOverride'

  const isOverrideReasonValid = OverrideReasonSchema.safeParse(
    reason.trim()
  ).success

  const reasonFieldId =
    variant === 'userElevatedHighRisk'
      ? 'user-elevated-high-risk-override-reason'
      : variant === 'disagreeAutomatedNotHighRisk'
        ? 'not-high-risk-disagree-reason'
        : 'high-risk-override-reason'

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-xl'>
        <DialogHeader>
          <DialogTitle>{t(`${keyPrefix}.dialogTitle`)}</DialogTitle>
        </DialogHeader>
        <p className='text-sm text-muted-foreground'>
          {t(`${keyPrefix}.dialogDescription`)}
        </p>
        <div className='space-y-2'>
          <Label htmlFor={reasonFieldId}>{t(`${keyPrefix}.reasonLabel`)}</Label>
          <Textarea
            id={reasonFieldId}
            value={reason}
            onChange={(event) => onReasonChange(event.target.value)}
            placeholder={t(`${keyPrefix}.reasonPlaceholder`)}
            rows={5}
            disabled={isPending}
          />
        </div>
        <DialogFooter className='flex flex-wrap gap-2 sm:justify-end'>
          <Button
            type='button'
            variant='outline'
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            {t(`${keyPrefix}.cancel`)}
          </Button>
          {onAlignWithAutomated ? (
            <Button
              type='button'
              variant='outline'
              onClick={onAlignWithAutomated}
              disabled={isPending}
            >
              {t(`${keyPrefix}.alignWithAutomatedButton`)}
            </Button>
          ) : null}
          <Button
            type='button'
            disabled={!isOverrideReasonValid || isPending}
            onClick={onSubmit}
          >
            {isPending
              ? t(`${keyPrefix}.submitting`)
              : t(`${keyPrefix}.submit`)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
